import { useState, useEffect, useRef } from 'react';

const useStoryTimer = (duration, isActive, onComplete, resetKey, isPaused = false) => {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef(null);
  const pausedTimeRef = useRef(0);
  const pauseStartRef = useRef(null);
  const animationFrameRef = useRef(null);
  const savedCallback = useRef(onComplete);

  // Keep onComplete fresh
  useEffect(() => {
    savedCallback.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!isActive || !duration) {
      setProgress(0);
      startTimeRef.current = null;
      pausedTimeRef.current = 0;
      pauseStartRef.current = null;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }

    // Handle pause state
    if (isPaused) {
      if (!pauseStartRef.current) {
        pauseStartRef.current = performance.now();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }

    // Resuming from pause
    if (pauseStartRef.current) {
      pausedTimeRef.current += performance.now() - pauseStartRef.current;
      pauseStartRef.current = null;
    }

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current - pausedTimeRef.current;
      const newProgress = Math.min((elapsed / duration) * 100, 100);

      setProgress(newProgress);

      if (newProgress < 100) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        if (savedCallback.current) savedCallback.current();
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, duration, resetKey, isPaused]);

  return progress;
};

export default useStoryTimer;
